<template>
  <div class="activity-history-grid-container">
    <Grid
      class="activity-history-grid"
      :data-items="filterRecords"
      :columns="controller.columns.value"
      :loader="loading"
      :resizable="true"
      data-cy="activity-history-grid"
    >
      <template v-slot:ActionCell="{ props: { dataItem, field } }">
        <td v-if="hasAttachments(dataItem)" class="k-table-td">
          <a
            class="action-attachment-link"
            @click="downloadAttachments(dataItem)"
            >{{
              `Download ${dataItem.NumDocsUploaded} Document${
                dataItem.NumDocsUploaded > 1 ? "s" : ""
              }`
            }}</a
          >
        </td>
        <td v-else class="k-table-td">
          {{ dataItem[field] }}
        </td>
      </template>
    </Grid>
    <div class="activity-history__show-all">
      <input
        type="checkbox"
        id="isShowAll"
        v-model="isShowAll"
        @change="activityShow"
      />
      <label for="isShowAll"
        >{{ isShowAll ? "Hide" : "Show" }} Assignment, Status, Personal Comment
        and Urgent History</label
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/stores/useMainStore";
import { ref, computed } from "vue";
import GridController from "@/shared/services/GridController";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { WfAttachmentDisplay } from "@/Domain/WfAttachmentDisplay";
import { EnumWorkflowType } from "@/Domain/EnumWorkflowType";
import { api } from "@/api";
import type { WfActionDisplay } from "@/Domain/WfActionDisplay";
import type { WfSubmissionDisplay } from "@/Domain/WfSubmissionDisplay";

const main = useMainStore();

const props = defineProps<{
  activityHistory: WfActionDisplay[];
  loading: boolean;
  jobId: string;
  workflowId: number;
  submissionType: EnumWorkflowType;
  showAll?: boolean;
}>();

const emit = defineEmits<{
  (e: "itemsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
}>();

const isShowAll = ref<boolean>(props.showAll ?? false);

const columns = [
  {
    title: "Date",
    field: "Timestamp",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm a}",
    //width: "200",  TODO maybe revisit later. objective is to give more relative width to the Action String column
  },
  {
    title: "Who",
    field: "ModifiedBy",
    //width: "250",
  },
  {
    title: "Action Type",
    field: "ActionString",
    cell: "ActionCell",
  },
] as GridColumnProps[];

const controller = new GridController<WfSubmissionDisplay>(
  "ActivityHistoryGrid",
  columns,
  emit,
  0
);

controller.initOptions();

const activityShow = function () {};

const hasAttachments = function (activity: WfActionDisplay) {
  return (
    activity.ActionString.includes("Document Uploaded") ||
    activity.ActionString.includes("Documents Uploaded")
  );
};

const downloadAttachments = function (activity: WfAttachmentDisplay) {
  return api.getDownload("GetWorkflowAttachmentFile", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    jobId: props.jobId,
    workflowId: props.workflowId,
    attachmentId: activity.Url,
    cartId: 0,
    workflowType:
      props.submissionType == EnumWorkflowType.Product
        ? "Product"
        : "Ingredient",
  });
};

const filterRecords = computed(() => {
  return props.activityHistory.filter(
    (activity) =>
      (isShowAll.value ||
        !(
          activity.ActionString.startsWith("Assigned To:") || // exclude when isShowAll is unchecked
          activity.ActionString.startsWith("Status Updated To:") ||
          activity.ActionString.startsWith("Urgent Updated To:") ||
          activity.ActionString.startsWith("Private Comment") ||
          (activity.ActionString.startsWith("Comment Added:") &&
            activity.ActionString.includes("ing status updated to:"))
        )) &&
      !activity.ActionString.includes("Comment added systematically")
  ); // allways exclude
});
</script>

<style scoped lang="scss">
.k-grid {
  .action-attachment-link {
    color: #337ab7;
    cursor: pointer;

    &:hover {
      color: #0a58ca;
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
  .k-grid th.k-table-th.k-header {
    background: #dedede;
    font: #999;
    font-weight: 700px;
  }
  .k-grid-header {
    background: #dedede;
  }
}

.activity-history-grid-container {
  padding: 0px;
}

.activity-history__show-all {
  border: 1px solid #e8e8e8;
  border-top: none;
  background: #efefef;
  align-items: center;
  font-size: 12px;
  display: flex;
  align-items: flex-end;
}
</style>
